import {createAsyncThunk} from '@reduxjs/toolkit'
import {clearOfferCheckOverrideUrl} from 'modules/common/actions/offerCheckOverride'
import Router from 'next/router'
import {
  BookingLatencyOutcome,
  markBookingHandoff,
  trackBookingLatency
} from 'performance/bookingLatency'
import getNewLocationUrl from 'utils/getNewLocationUrl'
import {PAYMENT_PAGE_PATH} from 'utils/urls'

import {replaceLocation} from '@vio/core/src/_web/utils/browser/navigateTo'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {BookingStatusLink} from '@vio/core/src/api-types/bovio/response/booking_status'
import {LinkType} from '@vio/core/src/booking/types/Link'
import {isFlutterAppWebView} from '@vio/core/src/native'

import {RootState} from '../../../store'
import {redirect, reset} from '../slice'

export interface HandlePaymentRedirectArg {
  paymentRedirectLink: BookingStatusLink | LinkType
  paymentMethodType?: string
  bookingRequestId: string
  /**
   * The booking request id does not survive the redirect, so the booking id is
   * what joins this trip to the one measured on the resume page.
   */
  bookingId?: string
}

export const handlePaymentRedirect = createAsyncThunk<
  void,
  HandlePaymentRedirectArg,
  {state: RootState}
>('bookingCreation/handlePaymentRedirect', async (arg, {dispatch}) => {
  const {paymentRedirectLink, paymentMethodType, bookingRequestId, bookingId} =
    arg

  dispatch(redirect())
  markBookingHandoff(BookingLatencyOutcome.PaymentRedirect, bookingId)
  trackBookingLatency({
    dispatch,
    outcome: BookingLatencyOutcome.PaymentRedirect,
    bookingId,
    paymentMethod: paymentMethodType
  })
  dispatch(
    trackEvent({
      category: Category.User,
      entity: Entity.PaymentMethods,
      action: Action.Redirected,
      payload: {
        redirectUrl: paymentRedirectLink.href,
        paymentMethod: paymentMethodType,
        bookingRequestId
      }
    })
  )

  // [flutter app] ResumeFlow will be visible if redirect does not land back in app
  if (bookingId && isFlutterAppWebView()) {
    await Router.replace(
      getNewLocationUrl({
        url: PAYMENT_PAGE_PATH,
        search: {
          bookingId,
          paymentMethodType,
          requestId: bookingRequestId,
          resumeFlow: 'true',
          processing: undefined
        }
      }),
      undefined,
      {shallow: true}
    )
    dispatch(clearOfferCheckOverrideUrl())
    dispatch(reset())
  }

  replaceLocation(paymentRedirectLink.href)
})
